import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash.isequal';
import { useLocation } from 'react-router-dom';
import { format, formatISO, parseISO } from 'date-fns';

import { Grid } from '@material-ui/core/';
import { CloudUpload } from '@material-ui/icons';

import { Formik } from 'formik';
import {
  Page,
  LoadMask,
  Paginate,
  EmptyStateWithAction,
  Button,
  DialogModal,
  showSuccessToast,
  showErrorToast,
} from '../../../../../components';
import Export from './Export';
import { Colors } from '../../../../../constants';
import MaterialItem from './materialItem';
import Filters from './filters';

import {
  searchType,
  filtersInitialState,
  fieldNames,
  orderByType,
} from '../../../../../constants/stock/filters';
import FilterIndicator from '../../../../../components/FilterIndicator';
import { ITEMS_PER_PAGE } from '../../../../../constants/Paginate';
import { useDidUpdateEffect } from '../../../../../utils/hooks/did-update-effect';
import { HttpService, StockService } from '../../../../../services';
import { getNumberOfAppliedFilters } from '../../../../../utils';
import Spinner from '../../../../../components/Spinner';

const SpinnerContainer = styled.div`
  display: flex;
  padding: 0 56px;
  align-items: center;
  justify-content: center;
`;

const StyledEmptyStateWithAction = styled(EmptyStateWithAction)`
  padding: 40px 16px;
  border-bottom: 1px solid ${Colors.GRAY_LIGHTER};
  flex-direction: column;
`;

const StyledEmptyActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const orderByInitialState = {
  field: orderByType.ALPHABETICAL,
};
const searchInitialState = {
  type: undefined,
  term: '',
};

export const updateQueryStringInStockUrl = (
  history,
  {
    filters, search, orderBy, skip, take,
  },
) => {
  history.push(
    `?${HttpService.serializeQueryString({
      dueFrom: filters.dueDateRange.from
        ? formatISO(filters.dueDateRange.from, { representation: 'date' })
        : null,
      dueTo: filters.dueDateRange.to
        ? formatISO(filters.dueDateRange.to, { representation: 'date' })
        : null,
      materialType: Object.keys(filters.materialType).map((key) => filters.materialType[key] ? key : null,
      ),
      availability: filters.availability,
      searchTerm: search.term,
      take,
      skip,
      orderBy: orderBy.field,
    })}`,
  );
};

const StockLayout = ({
  userData,
  title,
  onRequestMaterialList,
  onRequestMaterialBatches,
  materialList,
  companyDialogList,
  handleOpenCompanyDialog,
  handleOutputCompanyDialog,
  loadingDialog,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [appliedFilters, setAppliedFilters] = useState(filtersInitialState);
  const [countAppliedFilters, setCountAppliedFilters] = useState(0);
  const [orderBy, setOrderBy] = useState(orderByInitialState);
  const [search, setSearch] = useState(searchInitialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const exportPayloadRef = useRef(null);

  const [materialIdsList, setMaterialIdsList] = useState([]);
  const [materialIdsLoading, setMaterialIdsLoading] = useState(false);
  const [materialIdsHasMore, setMaterialIdsHasMore] = useState(false);
  const [materialIdsSearch, setMaterialIdsSearch] = useState('');

  const [materialNamesList, setMaterialNamesList] = useState([]);
  const [materialNamesLoading, setMaterialNamesLoading] = useState(false);
  const [materialNamesHasMore, setMaterialNamesHasMore] = useState(false);
  const [materialNamesSearch, setMaterialNamesSearch] = useState('');

  const { search: locationSearch } = useLocation();

  const handleExportPayloadChange = useCallback((payload) => {
    exportPayloadRef.current = payload;
  }, []);

  const fetchMaterialIds = useCallback(
    async ({ lastId, search: searchParam } = {}) => {
      setMaterialIdsLoading(true);
      try {
        const response = await StockService.fetchMaterialIds({
          take: 10,
          lastId,
          search: searchParam || undefined,
        });
        const { items = [], hasMore = false } = response?.data?.data ?? {};
        if (lastId) {
          setMaterialIdsList((prev) => [...prev, ...items]);
        } else {
          setMaterialIdsList(items);
        }
        setMaterialIdsHasMore(hasMore);
      } catch (e) {
        console.error('Error fetching material ids', e);
      } finally {
        setMaterialIdsLoading(false);
      }
    },
    [],
  );

  const handleMaterialIdsLoadMore = useCallback(() => {
    if (
      materialIdsLoading
      || !materialIdsHasMore
      || materialIdsList.length === 0
    ) return;
    const lastItem = materialIdsList[materialIdsList.length - 1];
    fetchMaterialIds({ lastId: lastItem.id, search: materialIdsSearch });
  }, [
    materialIdsLoading,
    materialIdsHasMore,
    materialIdsList,
    materialIdsSearch,
    fetchMaterialIds,
  ]);

  const handleMaterialIdsSearch = useCallback(
    (searchParam) => {
      setMaterialIdsSearch(searchParam);
      fetchMaterialIds({ search: searchParam });
    },
    [fetchMaterialIds],
  );

  const fetchMaterialNames = useCallback(
    async ({ lastId, search: searchParam } = {}) => {
      setMaterialNamesLoading(true);
      try {
        const response = await StockService.fetchMaterialNames({
          take: 10,
          lastId,
          search: searchParam || undefined,
        });
        const { items = [], hasMore = false } = response?.data?.data ?? {};
        if (lastId) {
          setMaterialNamesList((prev) => [...prev, ...items]);
        } else {
          setMaterialNamesList(items);
        }
        setMaterialNamesHasMore(hasMore);
      } catch (e) {
        console.error('Error fetching material names', e);
      } finally {
        setMaterialNamesLoading(false);
      }
    },
    [],
  );

  const handleMaterialNamesLoadMore = useCallback(() => {
    if (
      materialNamesLoading
      || !materialNamesHasMore
      || materialNamesList.length === 0
    ) return;
    const lastItem = materialNamesList[materialNamesList.length - 1];
    fetchMaterialNames({ lastId: lastItem.id, search: materialNamesSearch });
  }, [
    materialNamesLoading,
    materialNamesHasMore,
    materialNamesList,
    materialNamesSearch,
    fetchMaterialNames,
  ]);

  const handleMaterialNamesSearch = useCallback(
    (searchParam) => {
      setMaterialNamesSearch(searchParam);
      fetchMaterialNames({ search: searchParam });
    },
    [fetchMaterialNames],
  );

  useEffect(() => {
    if (exportModalOpen) {
      setMaterialIdsSearch('');
      fetchMaterialIds();
      setMaterialNamesSearch('');
      fetchMaterialNames();
    }
  }, [exportModalOpen, fetchMaterialIds, fetchMaterialNames]);

  const handleExportConfirm = useCallback(async () => {
    const payload = exportPayloadRef.current;
    const materialsName = payload.materialFilters.productNames;
    const materialsCode = payload.materialFilters.productCodes;
    if (!payload) return;

    setExportLoading(true);
    const queryObject = HttpService.unserializeQueryString(locationSearch);
    const skip = queryObject.skip || 0;
    const take = queryObject.take || ITEMS_PER_PAGE;

    try {
      await StockService.fetchDownloadStockPosition({
        skip,
        take,
        fileName: `export${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.xlsx`,
        materialsName,
        materialsCode,
      });

      showSuccessToast('Relatório exportado com sucesso');
      setExportModalOpen(false);
    } catch (e) {
      showErrorToast(e.message || 'Erro ao exportar relatório');
    } finally {
      setExportLoading(false);
    }
  }, []);

  const _buildMaterialRequestFromQueryString = () => {
    const queryObject = HttpService.unserializeQueryString(locationSearch);
    const skip = queryObject.skip || 0;
    const take = queryObject.take || ITEMS_PER_PAGE;

    if (locationSearch === '') {
      return {
        filters: {
          ...filtersInitialState,
        },
        take,
        skip,
        orderBy: {
          ...orderByInitialState,
        },
        search: {
          ...searchInitialState,
        },
      };
    }

    return {
      filters: {
        dueDateRange: {
          from: queryObject.dueFrom ? parseISO(queryObject.dueFrom) : null,
          to: queryObject.dueTo ? parseISO(queryObject.dueTo) : null,
        },
        materialType: HttpService.unserializeQueryStringCheckboxObject(
          queryObject.materialType,
          String,
          filtersInitialState.materialType,
        ),
        availability: queryObject.availability,
      },
      take,
      skip,
      orderBy: {
        field: queryObject.orderBy,
      },
      search: {
        term: queryObject.searchTerm,
        type: queryObject.searchType,
      },
    };
  };

  const _onReloadDataTable = (filters, orderBy, search) => {
    const requestParams = {
      filters,
      take: itemsPerPage,
      skip: (currentPage - 1) * itemsPerPage,
      orderBy,
      search,
    };

    setCountAppliedFilters(
      getNumberOfAppliedFilters(filtersInitialState, filters),
    );
    onRequestMaterialList(requestParams);
  };

  useEffect(() => {
    const { filters, orderBy, search } = _buildMaterialRequestFromQueryString();

    setAppliedFilters(filters);
    setOrderBy(orderBy);
    setSearch(search);

    _onReloadDataTable(filters, orderBy, search);
    setIsInitialized(true);
  }, []);

  useDidUpdateEffect(() => {
    if (isInitialized) return;
    _onReloadDataTable(appliedFilters, orderBy, search);
  }, [
    currentPage,
    itemsPerPage,
    appliedFilters,
    isInitialized,
    orderBy,
    search,
  ]);

  const _onPageChange = (event, newPage) => {
    setIsInitialized(false);
    setCurrentPage(newPage);
  };

  const _onChangeItemsPerPage = (event, newItemsPerPage) => {
    setIsInitialized(false);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const _onFiltersChange = ({ orderBy, ...newFilters }) => {
    setIsInitialized(false);
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  const _onClearFilter = (handleReset) => {
    setIsInitialized(false);
    if (handleReset) handleReset();
    setAppliedFilters({ ...filtersInitialState });
    setCurrentPage(1);
  };

  const _onChangeDataOrder = (field) => {
    setIsInitialized(false);
    setOrderBy({
      field,
    });
  };

  const _downloadStockPosition = async () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    const queryObject = HttpService.unserializeQueryString(locationSearch);
    const skip = queryObject.skip || 0;
    const take = queryObject.take || ITEMS_PER_PAGE;

    try {
      await StockService.fetchDownloadStockPosition({
        skip,
        take,
        fileName: `export${format(new Date(), 'yyyy-MM-dd-HH-mm-ss')}.xlsx`,
      });

      showSuccessToast('Relatório exportado com sucesso');
    } catch (e) {
      showErrorToast(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  const _onClearSearch = (handleReset) => {
    setIsInitialized(false);
    if (handleReset) handleReset();
    setSearch({
      type: undefined,
      term: '',
    });
    _onClearFilter();
  };

  const _onSearch = ({ value, handleSubmit }) => {
    setAppliedFilters({
      ...filtersInitialState,
    });
    setSearch(value);
    handleSubmit();
    setCurrentPage(1);
  };

  return (
    <Page
      title={title}
      userData={userData}
      companyDialogList={companyDialogList}
      loadingDialog={loadingDialog}
      renderRight={() => (
        <>
          <Button
            size="large"
            color="secondary"
            variant="outlined"
            onClick={() => setExportModalOpen(true)}
            endIcon={<CloudUpload />}
            style={{ fontWeight: '500', marginRight: 8 }}
          >
            Exportar
          </Button>
        </>
      )}
      handleOpenCompanyDialog={handleOpenCompanyDialog}
      handleOutputCompanyDialog={(companyId) => {
        handleOutputCompanyDialog(companyId, () => {
          _onClearSearch();
        });
      }}
    >
      <>
        <Formik
          initialValues={{
            [fieldNames.AVAILABILITY]: appliedFilters[fieldNames.AVAILABILITY],
            [fieldNames.DUE_DATE]: appliedFilters[fieldNames.DUE_DATE],
            [fieldNames.TYPE]: appliedFilters[fieldNames.TYPE],
            [fieldNames.ORDER_BY]: orderBy.field,
          }}
          onSubmit={_onFiltersChange}
          enableReinitialize
        >
          {({
            handleReset, values, setValues, handleSubmit,
          }) => (
            <>
              <Filters
                userData={userData}
                values={values}
                onSearch={(value) => _onSearch({
                  value,
                  setValues,
                  handleSubmit,
                })}
                onChangeDataOrder={_onChangeDataOrder}
                listCountResults={materialList.list.length}
                appliedFilters={appliedFilters}
              />
              {(!isEqual(filtersInitialState, appliedFilters)
                || search.term) && (
                <FilterIndicator
                  onClear={() => _onClearFilter(handleReset)}
                  onClearSearch={() => _onClearSearch(handleReset)}
                  appliedFilters={countAppliedFilters}
                  searchTerm={search.term}
                  hideExport
                />
              )}
            </>
          )}
        </Formik>
        {materialList.list.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {materialList.list.map((obj) => (
                <Grid key={obj.id} item lg={3} md={4} sm={6} xs={12}>
                  <MaterialItem
                    userData={userData}
                    item={obj}
                    enableSearchBatches={materialList.enableSearchBatches}
                    onRequestMaterialBatches={onRequestMaterialBatches}
                  />
                </Grid>
              ))}
            </Grid>
            {materialList.total > itemsPerPage && (
              <Paginate
                currentPage={currentPage}
                totalItems={materialList.total}
                itemsPerPage={itemsPerPage}
                onPageChange={_onPageChange}
                onChangeItemsPerPage={_onChangeItemsPerPage}
              />
            )}
          </>
        ) : (
          <>
            {!materialList.loading && (
              <StyledEmptyStateWithAction
                alignCenter
                emptyStateText={
                  search.term && materialList.list.length <= 0
                    ? 'Nenhum material foi encontrado. Que tal tentar buscar de outra forma?'
                    : 'Nenhum material pra exibir :('
                }
                renderAction={() => (
                  <StyledEmptyActionsContainer>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        window.location.reload();
                      }}
                    >
                      Atualizar
                    </Button>
                  </StyledEmptyActionsContainer>
                )}
              />
            )}
          </>
        )}
      </>
      {materialList.loading && <LoadMask />}

      <DialogModal
        title="Exportar Relatórios"
        size="lg"
        isOpenDialog={exportModalOpen}
        handleCloseDialog={() => setExportModalOpen(false)}
        handleConfirmDialog={handleExportConfirm}
        confirmButtonText="Exportar"
        loadingDialog={exportLoading}
        hasBorder
        useModalSpacing
      >
        <Export
          onExportPayloadChange={handleExportPayloadChange}
          materialIds={materialIdsList}
          materialIdsLoading={materialIdsLoading}
          materialIdsHasMore={materialIdsHasMore}
          onMaterialIdsLoadMore={handleMaterialIdsLoadMore}
          onMaterialIdsSearch={handleMaterialIdsSearch}
          materialNames={materialNamesList}
          materialNamesLoading={materialNamesLoading}
          materialNamesHasMore={materialNamesHasMore}
          onMaterialNamesLoadMore={handleMaterialNamesLoadMore}
          onMaterialNamesSearch={handleMaterialNamesSearch}
        />
      </DialogModal>
    </Page>
  );
};

StockLayout.propTypes = {
  title: PropTypes.string.isRequired,
  userData: PropTypes.object,
  onRequestMaterialList: PropTypes.func,
  onRequestMaterialBatches: PropTypes.func,
  materialList: PropTypes.object,
  companyDialogList: PropTypes.object,
  loadingDialog: PropTypes.bool,
  handleOpenCompanyDialog: PropTypes.func,
  handleOutputCompanyDialog: PropTypes.func,
};

StockLayout.defaultProps = {
  userData: {},
  onRequestMaterialList: () => {},
  onRequestMaterialBatches: () => {},
  materialList: {},
  companyDialogList: {},
  loadingDialog: false,
  handleOpenCompanyDialog: () => {},
  handleOutputCompanyDialog: () => {},
};

export default StockLayout;
